import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Check, X, Clock, Users, Wrench, AlertCircle, CheckCircle2, CalendarDays } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Formatta data in formato italiano
const formatDateIT = (dateStr) => {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const ApprovalPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchApprovalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchApprovalData = async () => {
    try {
      const response = await axios.get(`${API}/public/approve/${token}`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Link non valido o scaduto');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/public/approve/${token}`, {
        action,
        notes
      });
      setResult({
        success: true,
        message: response.data.message,
        status: response.data.status
      });
    } catch (err) {
      setResult({
        success: false,
        message: err.response?.data?.detail || 'Errore durante l\'operazione'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1B6B93] to-[#4BA3C7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1B6B93] to-[#4BA3C7] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Errore</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1B6B93] to-[#4BA3C7] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            {result.success ? (
              <>
                <CheckCircle2 className={`w-16 h-16 mx-auto mb-4 ${result.status === 'approved' ? 'text-green-500' : 'text-orange-500'}`} />
                <h2 className="text-xl font-bold text-slate-800 mb-2">
                  {result.status === 'approved' ? 'Rapporto Approvato' : 'Rapporto Rifiutato'}
                </h2>
              </>
            ) : (
              <>
                <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
                <h2 className="text-xl font-bold text-slate-800 mb-2">Errore</h2>
              </>
            )}
            <p className="text-slate-600">{result.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (data?.already_processed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1B6B93] to-[#4BA3C7] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className={`w-16 h-16 mx-auto mb-4 ${data.action_taken === 'approve' ? 'text-green-500' : 'text-orange-500'}`} />
            <h2 className="text-xl font-bold text-slate-800 mb-2">
              Già elaborato
            </h2>
            <p className="text-slate-600">
              Questo rapporto è già stato {data.action_taken === 'approve' ? 'approvato' : 'rifiutato'}.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const report = data?.report;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B6B93] to-[#4BA3C7] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src={process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_5956ea83-268b-47c4-a704-9204051c5002/artifacts/w25p61k6_0splendor%20vettoriale%202020%20S%20filigrana.jpg"} 
            alt="Splendor Sud" 
            className="h-16 mx-auto"
          />
          <h1 className="text-white text-xl font-bold mt-2">Rapporti Lavoro Straordinario</h1>
        </div>

        <Card className="shadow-xl">
          <CardHeader className="bg-[#1B6B93] text-white rounded-t-lg">
            <CardTitle className="text-center">
              Richiesta di Approvazione
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Report Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold text-slate-600">N° Rapporto:</span>
                <p className="text-slate-800">RLS n° {report?.report_number}</p>
              </div>
              <div>
                <span className="font-semibold text-slate-600">
                  {report?.is_multi_day ? 'Periodo:' : 'Data:'}
                </span>
                <p className="text-slate-800">
                  {report?.is_multi_day && report?.date_range 
                    ? report.date_range.replace(/(\d{4})-(\d{2})-(\d{2})/g, (m, y, mo, d) => `${d}/${mo}/${y}`)
                    : formatDateIT(report?.date)}
                </p>
              </div>
              <div>
                <span className="font-semibold text-slate-600">Cantiere:</span>
                <p className="text-slate-800">{data?.site_name}</p>
              </div>
              <div>
                <span className="font-semibold text-slate-600">Cliente:</span>
                <p className="text-slate-800">{data?.client_name || '-'}</p>
              </div>
              {/* Mostra orario solo se NON è multi-giorno */}
              {!report?.is_multi_day && (
                <div>
                  <span className="font-semibold text-slate-600">Orario:</span>
                  <p className="text-slate-800">{report?.start_time} - {report?.end_time}</p>
                </div>
              )}
              {/* Mostra ore totali solo se client_report_no_hours è false */}
              {!report?.client_report_no_hours && (
                <div>
                  <span className="font-semibold text-slate-600">Ore Totali:</span>
                  <p className="text-slate-800 font-bold">h {report?.total_hours?.toFixed(2)}</p>
                </div>
              )}
              {/* Mostra numero giornate per multi-giorno */}
              {report?.is_multi_day && (
                <div>
                  <span className="font-semibold text-slate-600">N° Giornate:</span>
                  <p className="text-slate-800">{report?.num_workdays || report?.workdays?.length || '-'}</p>
                </div>
              )}
            </div>

            {/* Dettaglio giornate per multi-giorno */}
            {report?.is_multi_day && report?.workdays?.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarDays className="w-4 h-4 text-[#1B6B93]" />
                  <span className="font-semibold text-slate-700">Dettaglio Giornate</span>
                </div>
                <div className="space-y-2">
                  {report.workdays.map((wd, idx) => (
                    <div key={idx} className="flex justify-between items-center text-sm border-b border-slate-200 pb-2 last:border-0">
                      <div>
                        <span className="font-medium">{formatDateIT(wd.date)}</span>
                        {!report?.client_report_no_hours && (
                          <span className="text-slate-500 ml-2">({wd.start_time} - {wd.end_time})</span>
                        )}
                      </div>
                      {!report?.client_report_no_hours && (
                        <span className="font-semibold text-[#1B6B93]">h {(wd.day_total_hours || 0).toFixed(2)}</span>
                      )}
                      {report?.client_report_no_hours && (
                        <span className="text-slate-500 text-xs">{wd.employees?.length || 0} dipendenti</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Special Hours - solo se non nascosto */}
            {!report?.client_report_no_hours && (report?.night_hours > 0 || report?.semifestive_hours > 0 || report?.festive_hours > 0) && (
              <div className="flex flex-wrap gap-2">
                {report?.night_hours > 0 && (
                  <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs">
                    🌙 Nott: h {report.night_hours.toFixed(2)}
                  </span>
                )}
                {report?.semifestive_hours > 0 && (
                  <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs">
                    ⚡ Semif: h {report.semifestive_hours.toFixed(2)}
                  </span>
                )}
                {report?.festive_hours > 0 && (
                  <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                    🎉 Fest: h {report.festive_hours.toFixed(2)}
                  </span>
                )}
              </div>
            )}

            {/* Activity */}
            <div>
              <span className="font-semibold text-slate-600">Attività:</span>
              <p className="text-slate-800">{report?.activity_type}</p>
            </div>

            {/* Reference */}
            {report?.ticket && (
              <div>
                <span className="font-semibold text-slate-600">Ns./Vs. Rif.:</span>
                <p className="text-slate-800">{report.ticket}</p>
              </div>
            )}

            {/* Notes */}
            {report?.notes && (
              <div>
                <span className="font-semibold text-slate-600">Note:</span>
                <p className="text-slate-800">{report.notes}</p>
              </div>
            )}

            {/* Employees - solo per rapporti singoli (multi-giorno ha già dettaglio nelle giornate) */}
            {!report?.is_multi_day && report?.employees?.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-[#1B6B93]" />
                  <span className="font-semibold text-slate-600">Dipendenti ({report.employees.length})</span>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  {report.employees.map((emp, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{emp.name} ({emp.matricola})</span>
                      {!report?.client_report_no_hours && (
                        <span className="text-slate-600">
                          {emp.start_time}-{emp.end_time} • h {emp.hours?.toFixed(2)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Equipment - nascosto se client_report_no_hours */}
            {!report?.client_report_no_hours && report?.equipment?.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Wrench className="w-4 h-4 text-[#4BA3C7]" />
                  <span className="font-semibold text-slate-600">Attrezzature ({report.equipment.length})</span>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  {report.equipment.map((eq, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{eq.equipment_name}</span>
                      <span className="text-slate-600">h {eq.hours?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Approval Notes */}
            <div className="border-t pt-4">
              <Label>Note (opzionale)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Aggiungi eventuali note..."
                className="mt-2"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button
                onClick={() => handleAction('approve')}
                disabled={submitting}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Approva
              </Button>
              <Button
                onClick={() => handleAction('reject')}
                disabled={submitting}
                variant="destructive"
                className="flex-1"
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <X className="w-4 h-4 mr-2" />
                )}
                Rifiuta
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-white/70 text-sm mt-6">
          Splendor Sud S.r.l. - Sanificazioni e Pulizie dal 1970
        </p>
      </div>
    </div>
  );
};

export default ApprovalPage;
